/**
 * Writing a proposal on the page it will be read on.
 *
 * Three columns: the blocks to add on the left, the business's paper in the
 * middle, and the picked block's settings on the right. The paper is drawn
 * with the customer's own components and palette, so what the office types
 * into is what the customer opens — the letterhead, the headings, the
 * package cards and the totals. Below 1023px the two side columns become
 * sheets behind a bar at the bottom.
 *
 * Blocks are dragged in from the library, dragged by their grip to reorder, or
 * added with the + between any two. The cover is pinned first because every
 * reader of a proposal treats only a leading cover as a cover. Every change
 * goes through `useDraftHistory`, so Cmd+Z takes back a drag, a deletion or
 * an accepted suggestion the same way it takes back typing.
 *
 * Same component for a quote on a job and a template in Settings; the
 * builder around it decides where the draft is saved. Decisions 80 and 81.
 */
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { letterDate, type QuoteSectionKindKey } from '@fieldops/shared'
import { useEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDate } from '../../lib/format.js'
import { errorMessage } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Sheet } from '../Sheet.js'
import { applySuggestion, useAssist, type AssistRequest, type Suggestion } from './AssistMenu.js'
import { GHOST_PRICING, BlockInspector } from './BlockInspector.js'
import { BLOCK_KINDS, BlockLibrary, LIBRARY_PREFIX, LibraryGhost, SINGLE_BLOCKS } from './BlockLibrary.js'
import { BLOCK_ICON, CanvasBlock, HeadingInput } from './CanvasBlock.js'
import { SECTION_LABEL, blankSection, draftAsView, newKey, type DraftSection, type ProposalDraft, type ProposalMeta } from './draft.js'
import { Letterhead, themeOf, themeVars, type LetterheadBusiness } from './Letterhead.js'
import { EditablePricing, type LineEditing } from './CanvasLines.js'
import type { Choice } from './ProposalPage.js'
import { useDraftHistory, type DraftUpdate } from './useDraftHistory.js'

const END = 'canvas-end'

export function ProposalCanvas({
  draft,
  update,
  mode,
  clientId,
  jobId,
  business,
  meta,
  assistTarget,
  beforeAssist,
}: {
  draft: ProposalDraft
  update: DraftUpdate
  mode: 'quote' | 'template'
  /** Prices catalog items at this client's tier. Null in a template. */
  clientId: string | null
  /** Where a photo is filed. Null in a template, which files it as `proposal`. */
  jobId: string | null
  business: LetterheadBusiness
  meta: ProposalMeta
  assistTarget: { kind: 'quote'; quoteId: string } | { kind: 'template'; name: string }
  beforeAssist?: () => Promise<unknown>
}) {
  const history = useDraftHistory(draft, update)
  const { apply } = history
  const [selected, setSelected] = useState<string | null>(null)
  const [focusOption, setFocusOption] = useState<string | null>(null)
  const [sheet, setSheet] = useState<'library' | 'inspector' | null>(null)
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  const [dragKind, setDragKind] = useState<QuoteSectionKindKey | null>(null)
  const assist = useAssist({ target: assistTarget, draft, beforeAssist })
  const latest = useRef(draft)
  latest.current = draft

  const view = useMemo(() => draftAsView(draft, meta), [draft, meta])
  const present = useMemo(() => new Set(draft.sections.map((section) => section.kind)), [draft.sections])
  const cover = draft.sections[0]?.kind === 'COVER' ? draft.sections[0] : null
  const flow = cover ? draft.sections.slice(1) : draft.sections
  const flowKeys = flow.map((section) => section.key)

  // A block that was removed — or undone out of existence — is not picked.
  useEffect(() => {
    if (selected && selected !== GHOST_PRICING && !draft.sections.some((section) => section.key === selected)) {
      setSelected(null)
    }
    if (suggestion && !draft.sections.some((section) => section.key === suggestion.sectionKey)) setSuggestion(null)
  }, [draft.sections, selected, suggestion])

  const patch = (key: string, next: Partial<DraftSection>, group?: string) =>
    apply(
      (d) => ({ ...d, sections: d.sections.map((section) => (section.key === key ? { ...section, ...next } : section)) }),
      group,
    )

  /** Adds a block; `at` is a position among the non-cover blocks. */
  const insert = (kind: QuoteSectionKindKey, at?: number) => {
    if (SINGLE_BLOCKS.has(kind) && present.has(kind)) return
    const section = blankSection(kind)
    apply((d) => {
      if (kind === 'COVER') return { ...d, sections: [section, ...d.sections] }
      const head = d.sections[0]?.kind === 'COVER' ? 1 : 0
      const rest = d.sections.slice(head)
      let index = at ?? -1
      if (index < 0) {
        const after = rest.findIndex((entry) => entry.key === selected)
        const terms = rest.findIndex((entry) => entry.kind === 'TERMS')
        index = kind === 'TERMS' ? rest.length : after >= 0 ? after + 1 : terms >= 0 ? terms : rest.length
      }
      const next = [...rest.slice(0, index), section, ...rest.slice(index)]
      return { ...d, sections: [...d.sections.slice(0, head), ...next] }
    })
    setSelected(section.key)
    setSheet(null)
  }

  const move = (key: string, by: -1 | 1) =>
    apply((d) => {
      const head = d.sections[0]?.kind === 'COVER' ? 1 : 0
      const index = d.sections.findIndex((section) => section.key === key)
      const target = index + by
      if (index < head || target < head || target >= d.sections.length) return d
      return { ...d, sections: arrayMove(d.sections, index, target) }
    })

  const duplicate = (key: string) =>
    apply((d) => {
      const index = d.sections.findIndex((section) => section.key === key)
      const source = d.sections[index]
      if (!source) return d
      const copy = { ...source, key: newKey('s') }
      return { ...d, sections: [...d.sections.slice(0, index + 1), copy, ...d.sections.slice(index + 1)] }
    })

  const remove = (key: string) => {
    apply((d) => ({ ...d, sections: d.sections.filter((section) => section.key !== key) }))
    if (selected === key) setSelected(null)
  }

  const ask = async (request: AssistRequest) => {
    const section = latest.current.sections.find((entry) => entry.key === request.sectionKey)
    if (!section) return
    setSuggestion({ ...request, status: 'loading', text: null, error: null })
    try {
      const text = await assist.ask(section, request)
      setSuggestion((current) =>
        current?.sectionKey === request.sectionKey ? { ...request, status: 'done', text, error: null } : current,
      )
    } catch (error) {
      setSuggestion((current) =>
        current?.sectionKey === request.sectionKey
          ? { ...request, status: 'failed', text: null, error: errorMessage(error) }
          : current,
      )
    }
  }

  const accept = (how: 'replace' | 'insert') => {
    if (!suggestion || suggestion.status !== 'done') return
    const taken = suggestion
    apply((d) => ({
      ...d,
      sections: d.sections.map((section) =>
        section.key === taken.sectionKey ? { ...section, body: applySuggestion(section.body, taken, how) } : section,
      ),
    }))
    setSuggestion(null)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const onDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id)
    if (id.startsWith(LIBRARY_PREFIX)) setDragKind(id.slice(LIBRARY_PREFIX.length) as QuoteSectionKindKey)
  }

  const onDragEnd = (event: DragEndEvent) => {
    setDragKind(null)
    const { active, over } = event
    if (!over) return
    const id = String(active.id)
    const overId = String(over.id)

    if (id.startsWith(LIBRARY_PREFIX)) {
      const kind = id.slice(LIBRARY_PREFIX.length) as QuoteSectionKindKey
      if (overId === END) return insert(kind, flow.length)
      const index = flowKeys.indexOf(overId)
      if (index < 0) return insert(kind)
      const dragged = active.rect.current.translated
      const below = dragged ? dragged.top + dragged.height / 2 > over.rect.top + over.rect.height / 2 : false
      return insert(kind, below ? index + 1 : index)
    }

    if (id === overId || !flowKeys.includes(overId)) return
    apply((d) => {
      const from = d.sections.findIndex((section) => section.key === id)
      const to = d.sections.findIndex((section) => section.key === overId)
      if (from < 0 || to < 0) return d
      return { ...d, sections: arrayMove(d.sections, from, to) }
    })
  }

  const select = (key: string | null) => {
    setSelected(key)
    if (key === null) setFocusOption(null)
  }

  const lineEditing: LineEditing = {
    lines: draft.lines,
    mode,
    onPatch: (key, next, group) =>
      apply((d) => ({ ...d, lines: d.lines.map((line) => (line.key === key ? { ...line, ...next } : line)) }), group),
    onRemove: (key) => apply((d) => ({ ...d, lines: d.lines.filter((line) => line.key !== key) })),
    onAdd: (line) => apply((d) => ({ ...d, lines: [...d.lines, line] })),
  }

  const blockProps = (section: DraftSection, index: number, handle: HTMLAttributes<HTMLButtonElement> | null) => ({
    lineEditing,
    section,
    selected: selected === section.key,
    onSelect: () => select(section.key),
    onPatch: (next: Partial<DraftSection>, group?: string) => patch(section.key, next, group),
    view,
    business,
    number: meta.number,
    date: letterDate(meta.date),
    defaultTerms: draft.termsText,
    mode,
    jobId,
    onOptionFocus: (key: string) => {
      setSelected(section.key)
      setFocusOption(key)
    },
    controls: {
      canMoveUp: section.kind !== 'COVER' && index > 0,
      canMoveDown: section.kind !== 'COVER' && index < flow.length - 1,
      onMove: (by: -1 | 1) => move(section.key, by),
      onDuplicate: section.kind === 'TEXT' || section.kind === 'IMAGE' ? () => duplicate(section.key) : null,
      onRemove: () => remove(section.key),
      handle,
    },
    assist: {
      available: assist.available,
      suggestion: suggestion?.sectionKey === section.key ? suggestion : null,
      onAsk: (action: AssistRequest['action'], instruction: string | null, selection: AssistRequest['selection']) =>
        void ask({ sectionKey: section.key, action, instruction, selection }),
      onReplace: () => accept('replace'),
      onInsert: () => accept('insert'),
      onRetry: () => {
        if (suggestion) void ask(suggestion)
      },
      onDiscard: () => setSuggestion(null),
    },
  })

  const inspector = (
    <BlockInspector
      draft={draft}
      apply={apply}
      selected={selected}
      onSelect={select}
      onPatch={patch}
      onRemove={remove}
      mode={mode}
      clientId={clientId}
      jobId={jobId}
      focusOption={focusOption}
      aiAvailable={assist.available}
    />
  )

  const picked = draft.sections.find((section) => section.key === selected)
  const pickedName = selected === GHOST_PRICING ? 'Pricing' : picked ? SECTION_LABEL[picked.kind] : 'Document'
  const pickedIcon = selected === GHOST_PRICING ? 'pricing' : picked ? BLOCK_ICON[picked.kind] : 'quotes'

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div
        className="flex min-h-0 flex-1"
        style={themeVars(themeOf(business))}
        onKeyDown={(event) => {
          if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return
          event.preventDefault()
          if (event.shiftKey) history.redo()
          else history.undo()
        }}
      >
        <aside
          aria-label="Add blocks"
          className="hidden w-[232px] shrink-0 flex-col gap-3 overflow-y-auto border-r border-ink-200 bg-ink-50 p-3 lg:flex"
        >
          <p className="px-1 text-[10px] font-bold tracking-[0.06em] text-ink-500 uppercase">Blocks</p>
          <BlockLibrary present={present} onAdd={(kind) => insert(kind)} />
          <p className="px-1 text-[11px] leading-4 text-ink-500">Drag one onto the page, or click to add it after the block you are on.</p>
        </aside>

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-1 border-b border-ink-200 bg-white px-3 py-1.5">
            <HistoryButton label="Undo" icon="undo" shortcut="⌘Z" disabled={!history.canUndo} onClick={history.undo} />
            <HistoryButton label="Redo" icon="redo" shortcut="⇧⌘Z" disabled={!history.canRedo} onClick={history.redo} />
            <span className="ml-2 hidden truncate text-xs text-ink-500 sm:inline">
              {assist.available ? 'Click any words to edit them. ✦ on a block for writing help.' : 'Click any words to edit them.'}
            </span>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto bg-[radial-gradient(circle_at_1px_1px,var(--color-ink-200)_1px,transparent_0)] bg-ink-100 [background-size:20px_20px] px-3 py-6 pb-24 sm:px-8 sm:py-10 lg:pb-10"
            onClick={(event) => {
              if (!(event.target as HTMLElement).closest('[data-block], [data-keep-selection]')) select(null)
            }}
          >
            <article
              aria-label="Proposal page"
              className="mx-auto w-full max-w-[820px] overflow-visible rounded-md bg-white font-proposal text-[15px] text-ink-800 shadow-[0_1px_2px_rgb(0_0_0/0.06),0_24px_48px_-24px_rgb(0_0_0/0.3)]"
            >
              {cover ? (
                <CanvasBlock {...blockProps(cover, 0, null)} />
              ) : (
                <div className="h-2 rounded-t-md bg-[var(--p-brand)]" />
              )}
              <div className={cn('px-6 sm:px-14', cover ? 'pt-2 pb-8 sm:pb-12' : 'py-8 sm:py-12')}>
                {cover ? null : (
                  <>
                    <Letterhead business={business} number={meta.number} date={letterDate(meta.date)} />
                    <div className="pt-6 pb-2">
                      <HeadingInput
                        label="Page title"
                        value={draft.title}
                        placeholder="Title your proposal"
                        className="text-2xl leading-tight font-bold text-[var(--p-brand)] sm:text-[28px]"
                        onChange={(value) => apply((d) => ({ ...d, title: value }), 'doc-title')}
                      />

                      <dl
                        data-keep-selection
                        onClick={() => select(null)}
                        className="mt-5 grid cursor-pointer grid-cols-2 gap-x-6 gap-y-3 rounded-xl bg-[var(--p-brand-soft)]/60 px-5 py-4 text-sm ring-1 ring-[var(--p-brand-soft)] sm:grid-cols-4"
                      >
                        <Fact label="Prepared for" value={view.customerName} />
                        <Fact label="Property" value={view.siteAddress} />
                        <Fact label="Job" value={view.jobNumber || null} />
                        <Fact label="Valid until" value={view.validUntil ? formatDate(view.validUntil) : 'Not set'} />
                      </dl>
                    </div>
                  </>
                )}

                {!cover ? <InsertPoint present={present} onPick={(kind) => insert(kind, 0)} allowCover /> : null}

                <SortableContext items={flowKeys} strategy={verticalListSortingStrategy}>
                  {flow.map((section, index) => (
                    <FlowItem key={section.key}>
                      {section.kind === 'TERMS' && !present.has('PRICING') ? (
                        <GhostPricing
                          view={view}
                          editing={lineEditing}
                          selected={selected === GHOST_PRICING}
                          onSelect={() => select(GHOST_PRICING)}
                          onPlace={() => insert('PRICING', index)}
                        />
                      ) : null}
                      <SortableBlock id={section.key}>
                        {(handle) => <CanvasBlock {...blockProps(section, index, handle)} />}
                      </SortableBlock>
                      <InsertPoint present={present} onPick={(kind) => insert(kind, index + 1)} />
                    </FlowItem>
                  ))}
                </SortableContext>

                {!present.has('PRICING') && !present.has('TERMS') ? (
                  <GhostPricing
                    view={view}
                    editing={lineEditing}
                    selected={selected === GHOST_PRICING}
                    onSelect={() => select(GHOST_PRICING)}
                    onPlace={() => insert('PRICING', flow.length)}
                  />
                ) : null}

                {flow.length === 0 ? (
                  <EmptyPage onPick={(kind) => insert(kind)} present={present} />
                ) : null}

                <EndDrop active={dragKind !== null} />

                {!present.has('TERMS') && draft.termsText.trim() ? (
                  <section
                    data-keep-selection
                    onClick={() => select(null)}
                    className="cursor-pointer py-5 opacity-80 transition-opacity hover:opacity-100"
                  >
                    <h2 className="border-b-2 border-[var(--p-brand)] pb-1.5 text-lg font-bold text-ink-900">Terms</h2>
                    <p className="mt-3 text-xs whitespace-pre-line text-ink-600">{draft.termsText}</p>
                    <p className="mt-2 font-sans text-[11px] text-ink-400">Your default terms. Edit them under Document.</p>
                  </section>
                ) : null}
              </div>
            </article>
          </div>

          <div className="absolute inset-x-0 bottom-0 flex gap-2 border-t border-ink-200 bg-white/95 p-2 backdrop-blur lg:hidden">
            <button
              type="button"
              onClick={() => setSheet('library')}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-brand-800 text-sm font-semibold text-white"
            >
              <Icon name="plus" className="size-4" />
              Add a block
            </button>
            <button
              type="button"
              onClick={() => setSheet('inspector')}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl text-sm font-semibold text-ink-900 ring-1 ring-ink-300"
            >
              <Icon name={pickedIcon} className="size-4" />
              {pickedName} settings
            </button>
          </div>
        </div>

        <aside
          aria-label="Settings"
          className="hidden w-[340px] shrink-0 overflow-y-auto border-l border-ink-200 bg-white p-4 lg:block"
        >
          {inspector}
        </aside>
      </div>

      {sheet === 'library' ? (
        <Sheet title="Add a block" onClose={() => setSheet(null)}>
          <div className="p-3">
            <BlockLibrary present={present} onAdd={(kind) => insert(kind)} draggable={false} />
          </div>
        </Sheet>
      ) : null}
      {sheet === 'inspector' ? (
        <Sheet title="Settings" size="tall" onClose={() => setSheet(null)}>
          <div className="p-4" style={themeVars(themeOf(business))}>
            {inspector}
          </div>
        </Sheet>
      ) : null}

      <DragOverlay dropAnimation={null}>{dragKind ? <LibraryGhost kind={dragKind} /> : null}</DragOverlay>
    </DndContext>
  )
}

function FlowItem({ children }: { children: ReactNode }) {
  return <>{children}</>
}

function SortableBlock({ id, children }: { id: string; children: (handle: HTMLAttributes<HTMLButtonElement>) => ReactNode }) {
  const sortable = useSortable({ id })
  return (
    <div
      ref={sortable.setNodeRef}
      style={{ transform: CSS.Translate.toString(sortable.transform), transition: sortable.transition }}
      className={cn('relative', sortable.isDragging && 'z-20 opacity-90')}
    >
      {children({ ...sortable.attributes, ...sortable.listeners } as HTMLAttributes<HTMLButtonElement>)}
    </div>
  )
}

function HistoryButton({
  label,
  icon,
  shortcut,
  disabled,
  onClick,
}: {
  label: string
  icon: 'undo' | 'redo'
  shortcut: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={`${label} (${shortcut})`}
      disabled={disabled}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-md text-ink-600 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-30"
    >
      <Icon name={icon} className="size-4" />
    </button>
  )
}

function Fact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold tracking-[0.12em] text-[var(--p-accent-text)] uppercase">{label}</dt>
      <dd className="mt-0.5 font-semibold break-words whitespace-pre-line text-ink-900">{value}</dd>
    </div>
  )
}

/** The + between two blocks, and the little menu it opens. */
function InsertPoint({
  present,
  onPick,
  allowCover = false,
}: {
  present: ReadonlySet<QuoteSectionKindKey>
  onPick: (kind: QuoteSectionKindKey) => void
  allowCover?: boolean
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    return () => document.removeEventListener('pointerdown', away)
  }, [open])

  const kinds = BLOCK_KINDS.filter(
    (block) => !(SINGLE_BLOCKS.has(block.kind) && present.has(block.kind)) && (allowCover || block.kind !== 'COVER'),
  )

  return (
    <div ref={box} data-keep-selection className="group/insert relative -my-2 flex h-6 items-center justify-center">
      <span
        aria-hidden
        className={cn(
          'absolute inset-x-0 top-1/2 h-px bg-[var(--p-brand)] transition-opacity',
          open ? 'opacity-60' : 'opacity-0 group-hover/insert:opacity-40',
        )}
      />
      <button
        type="button"
        aria-label="Insert a block here"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'relative grid size-6 place-items-center rounded-full bg-white text-ink-500 shadow ring-1 ring-ink-200 transition-all hover:scale-110 hover:text-[var(--p-brand-text)] hover:ring-[var(--p-brand)]',
          open ? 'scale-110 opacity-100' : 'opacity-0 group-hover/insert:opacity-100 focus-visible:opacity-100',
        )}
      >
        <Icon name="plus" className="size-3.5" />
      </button>
      {open ? (
        <div
          role="menu"
          aria-label="Insert a block"
          className="absolute top-full left-1/2 z-30 mt-1 w-64 -translate-x-1/2 rounded-xl bg-white p-1.5 font-sans shadow-xl ring-1 ring-ink-200"
        >
          {kinds.map((block) => (
            <button
              key={block.kind}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onPick(block.kind)
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-ink-50"
            >
              <Icon name={BLOCK_ICON[block.kind]} className="size-4 text-ink-500" />
              <span className="text-sm font-medium text-ink-900">{block.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/** Where the price will print when there is no Pricing block. */
function GhostPricing({
  view,
  editing,
  selected,
  onSelect,
  onPlace,
}: {
  view: ReturnType<typeof draftAsView>
  editing: LineEditing
  selected: boolean
  onSelect: () => void
  onPlace: () => void
}) {
  const [choice, setChoice] = useState<Choice>({ optionId: null, addOnIds: [] })
  return (
    <div
      data-block={GHOST_PRICING}
      onClick={onSelect}
      className={cn(
        'relative -mx-4 cursor-pointer rounded-xl px-4 py-5 sm:-mx-6 sm:px-6',
        selected ? 'shadow-[0_0_0_2px_var(--p-brand)]' : 'shadow-[0_0_0_1px_var(--color-ink-200)] hover:shadow-[0_0_0_1px_var(--color-ink-300)]',
      )}
    >
      <div className="flex items-center gap-2 font-sans text-xs text-ink-500">
        <Icon name="pricing" className="size-3.5" />
        The price prints here automatically.
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onPlace()
          }}
          className="ml-auto rounded-md px-2 py-1 font-medium text-ink-700 ring-1 ring-ink-200 hover:bg-ink-50"
        >
          Give it a heading
        </button>
      </div>
      <h2 className="mt-3 border-b-2 border-[var(--p-brand)] pb-1.5 text-lg font-bold text-ink-900">
        {view.options.length > 0 ? 'Your options' : 'The work'}
      </h2>
      <EditablePricing view={view} editing={editing} choice={choice} onChoice={setChoice} />
    </div>
  )
}

function EmptyPage({
  onPick,
  present,
}: {
  onPick: (kind: QuoteSectionKindKey) => void
  present: ReadonlySet<QuoteSectionKindKey>
}) {
  return (
    <div data-keep-selection className="my-6 rounded-2xl px-6 py-10 text-center font-sans ring-2 ring-ink-200 ring-dashed">
      <p className="text-base font-semibold text-ink-900">A blank page</p>
      <p className="mt-1 text-sm text-ink-500">Start with a block. You can drag them about afterwards.</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {(['COVER', 'TEXT', 'IMAGE'] as const)
          .filter((kind) => !present.has(kind) || !SINGLE_BLOCKS.has(kind))
          .map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => onPick(kind)}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-800 shadow-sm ring-1 ring-ink-200 hover:ring-ink-400"
            >
              <Icon name={BLOCK_ICON[kind]} className="size-4" />
              {SECTION_LABEL[kind]}
            </button>
          ))}
      </div>
    </div>
  )
}

/** Dropping a block below the last one. */
function EndDrop({ active }: { active: boolean }) {
  const drop = useDroppable({ id: END })
  return (
    <div
      ref={drop.setNodeRef}
      className={cn(
        'mt-2 flex items-center justify-center rounded-xl font-sans text-xs transition-all',
        active ? 'h-16 text-ink-500 ring-2 ring-ink-200 ring-dashed' : 'h-4',
        drop.isOver && 'bg-[var(--p-brand-soft)] text-[var(--p-brand-text)] ring-[var(--p-brand)]',
      )}
    >
      {active ? 'Drop at the end' : null}
    </div>
  )
}
